import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { 
  Users, Search, Filter, ArrowLeft, Trash2, Pencil, Eye,
  Mail, Calendar, Building2, Crown, Shield, User as UserIcon,
  ChevronLeft, ChevronRight, RefreshCw, FileText, Briefcase,
  FolderOpen, Contact, Loader2, FlaskConical, Award, Rocket
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const planColors = {
  free: 'bg-gray-500/10 text-gray-500 border-gray-500/30',
  standard: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  premium: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  enterprise: 'bg-purple-500/10 text-purple-500 border-purple-500/30'
};

const planNames = {
  free: 'Free',
  standard: 'Standard',
  premium: 'Premium',
  enterprise: 'Enterprise'
};

// WO-71 — First Fifty (90 days) and Ambassador (12 months): Premium given free for a
// limited time. Each has its own colour and icon so they stand out in the list; the
// row also gets a coloured stripe on its left edge. Only the admin sees any of this.
const promoStyles = {
  ambassador: {
    label: 'Ambassador', Icon: Award,
    badge: 'bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400 border-fuchsia-500/40',
    row: 'border-l-4 border-l-fuchsia-500 bg-fuchsia-50/50 dark:bg-fuchsia-950/10',
    box: 'border-fuchsia-300 dark:border-fuchsia-800 bg-fuchsia-50/60 dark:bg-fuchsia-950/20',
  },
  first_fifty: {
    label: 'First Fifty', Icon: Rocket,
    badge: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/40',
    row: 'border-l-4 border-l-teal-500 bg-teal-50/50 dark:bg-teal-950/10',
    box: 'border-teal-300 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-950/20',
  },
};

const fmtDay = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

function PromoBadge({ promo, testid }) {
  if (!promo || !promoStyles[promo.kind]) return null;
  const st = promoStyles[promo.kind];
  const Icon = st.Icon;
  if (promo.status === 'invited') {
    return (
      <Badge variant="outline" className="text-muted-foreground border-dashed whitespace-nowrap" data-testid={testid}
        title={`Invited on ${fmtDay(promo.invited_at)} — the free Premium starts when they accept in the app.`}>
        <Icon className="w-3 h-3 mr-1" />{st.label} · invited
      </Badge>
    );
  }
  const tip = `${st.label}: Premium until ${fmtDay(promo.ends_at)} (${promo.days_left} days left), then back to ` +
    `${planNames[promo.original_plan] || 'Free'}. New paying users from their code this period: ${promo.paying_referrals}.`;
  return (
    <Badge variant="outline" className={`${st.badge} whitespace-nowrap`} title={tip} data-testid={testid}>
      <Icon className="w-3 h-3 mr-1" />
      {st.label} · {promo.days_left}d
      {promo.kind === 'ambassador' ? (promo.premium_only ? ' · Premium only' : ` · ${promo.paying_referrals} paying`) : ''}
    </Badge>
  );
}

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { language } = useLanguage();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  // WO-24 — counts over the WHOLE user base, sent by the server. The cards used
  // to count the fifteen rows of the page being looked at while "Total" counted
  // everybody, so the four numbers never added up.
  const [stats, setStats] = useState(null);
  
  // User detail dialog
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  
  // Plan change dialog
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [newPlan, setNewPlan] = useState('');
  const [updatingPlan, setUpdatingPlan] = useState(false);
  // WO-68 — Enterprise seat actions inside the plan dialog
  const [entBusy, setEntBusy] = useState(false);
  // WO-71 — First Fifty / Ambassador actions inside the plan dialog
  const [promoBusy, setPromoBusy] = useState(false);
  
  // Delete states
  const [deletingUser, setDeletingUser] = useState({});

  // Role change states
  const [changingRole, setChangingRole] = useState({});
  const [togglingInternal, setTogglingInternal] = useState({});

  // Report-credit refill states (Batch R1 §5)
  const [resettingCredits, setResettingCredits] = useState({});

  // Email change states
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [updatingEmail, setUpdatingEmail] = useState(false);

  // Bilingual helper (Arabic in step with English) for the role controls.
  const tt = (en, ar) => (language === 'ar' ? ar : en);

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      toast.error('Access denied. Admin only.');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [page, planFilter, roleFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', 15);
      
      if (searchQuery) params.append('search', searchQuery);
      if (planFilter && planFilter !== 'all') params.append('plan', planFilter);
      if (roleFilter && roleFilter !== 'all') params.append('role', roleFilter);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/users?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUsers(response.data.users);
      setTotal(response.data.total);
      setStats(response.data.stats || null);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchUsers();
  };

  const fetchUserDetails = async (userId) => {
    try {
      setLoadingUserDetails(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserDetails(response.data.user);
    } catch (err) {
      console.error('Error fetching user details:', err);
      toast.error('Failed to fetch user details');
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const openUserDetails = (userData) => {
    setSelectedUser(userData);
    setShowUserDialog(true);
    fetchUserDetails(userData.id);
  };

  const openPlanDialog = (userData) => {
    setSelectedUser(userData);
    setNewPlan(userData.plan_id || 'free');
    setShowPlanDialog(true);
  };

  // WO-71 — nominate First Fifty / Ambassador (or renew an Ambassador), or end it now.
  const handlePromo = async (action) => {
    if (!selectedUser) return;
    const url = `${BACKEND_URL}/api/admin/users/${selectedUser.id}/promo`;
    try {
      setPromoBusy(true);
      const res = action === 'end'
        ? await axios.delete(url, { headers: { Authorization: `Bearer ${token}` } })
        : await axios.post(url, { kind: action }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data?.message || 'Done', { duration: 8000 });
      setShowPlanDialog(false);
      fetchUsers();
    } catch (err) {
      console.error('First Fifty / Ambassador action failed:', err);
      toast.error(err.response?.data?.detail || 'The change failed', { duration: 10000 });
    } finally {
      setPromoBusy(false);
    }
  };

  // WO-68 — Enterprise is a seat on the company, never a personal plan. The
  // plan dialog now has its own Enterprise box: give a free (complimentary)
  // Enterprise, give one of the company's PAID seats, or remove the seat.
  const handleEnterpriseSeat = async (action) => {
    if (!selectedUser) return;
    const urls = {
      complimentary: { method: 'post', url: `${BACKEND_URL}/api/admin/users/${selectedUser.id}/complimentary-enterprise` },
      paid: { method: 'post', url: `${BACKEND_URL}/api/admin/users/${selectedUser.id}/enterprise-seat` },
      remove: { method: 'delete', url: `${BACKEND_URL}/api/admin/users/${selectedUser.id}/enterprise-seat` },
    };
    const call = urls[action];
    try {
      setEntBusy(true);
      const res = await axios({ method: call.method, url: call.url,
        headers: { Authorization: `Bearer ${token}` } });
      const msg = res.data?.message || 'Done';
      if (res.data?.personal_subscription_still_charging) {
        toast.warning(msg, { duration: 12000 });
      } else {
        toast.success(msg);
      }
      setShowPlanDialog(false);
      fetchUsers();
    } catch (err) {
      console.error('Enterprise seat action failed:', err);
      toast.error(err.response?.data?.detail || 'The Enterprise change failed');
    } finally {
      setEntBusy(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!selectedUser || !newPlan) return;
    
    try {
      setUpdatingPlan(true);
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${selectedUser.id}/plan`,
        { plan_id: newPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Plan updated to ${planNames[newPlan]}`);
      setShowPlanDialog(false);
      fetchUsers();
    } catch (err) {
      console.error('Error updating plan:', err);
      toast.error(err.response?.data?.detail || 'Failed to update plan');
    } finally {
      setUpdatingPlan(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      setDeletingUser(prev => ({ ...prev, [userId]: true }));
      await axios.delete(
        `${BACKEND_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      setChangingRole(prev => ({ ...prev, [userId]: true }));
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        newRole === 'admin'
          ? tt('User promoted to Admin', 'تمت ترقية المستخدم إلى مسؤول')
          : tt('User changed to Member', 'تم تغيير المستخدم إلى عضو')
      );
      fetchUsers();
    } catch (err) {
      console.error('Error changing role:', err);
      toast.error(err.response?.data?.detail || tt('Failed to change role', 'فشل تغيير الدور'));
    } finally {
      setChangingRole(prev => ({ ...prev, [userId]: false }));
    }
  };

  // WO-36 — "internal" = our own staff, test and demo accounts. They keep full
  // use of the app; they are only left out of the business figures on the
  // admin screens (revenue, users, funnel, usage). The change is audited.
  const handleToggleInternal = async (userData) => {
    const next = !userData.is_internal;
    try {
      setTogglingInternal(prev => ({ ...prev, [userData.id]: true }));
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${userData.id}/internal`,
        { is_internal: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(next
        ? `${userData.email} is now internal — left out of the business figures`
        : `${userData.email} counts as a customer again`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change the internal mark');
    } finally {
      setTogglingInternal(prev => ({ ...prev, [userData.id]: false }));
    }
  };

  const handleResetCredits = async (userId) => {
    try {
      setResettingCredits(prev => ({ ...prev, [userId]: true }));
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/reset-report-credits`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || tt('Monthly report credits reset', 'تمت إعادة ضبط رصيد التقارير الشهري'));
    } catch (err) {
      console.error('Error resetting report credits:', err);
      toast.error(err.response?.data?.detail || tt('Failed to reset report credits', 'فشل إعادة ضبط رصيد التقارير'));
    } finally {
      setResettingCredits(prev => ({ ...prev, [userId]: false }));
    }
  };

  const openEmailDialog = (userData) => {
    setEmailTarget(userData);
    setNewEmail(userData.email || '');
    setShowEmailDialog(true);
  };

  const handleUpdateEmail = async () => {
    if (!emailTarget || !newEmail.trim()) return;
    try {
      setUpdatingEmail(true);
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${emailTarget.id}/email`,
        { email: newEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(tt('Email address updated', 'تم تحديث عنوان البريد الإلكتروني'));
      setShowEmailDialog(false);
      fetchUsers();
    } catch (err) {
      console.error('Error updating email:', err);
      toast.error(err.response?.data?.detail || tt('Failed to update email', 'فشل تحديث البريد الإلكتروني'));
    } finally {
      setUpdatingEmail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/dashboard')}
          className="text-muted-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-indigo-500/10 flex items-center justify-center">
          <Users className="w-7 h-7 text-indigo-500" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Users Management</h1>
          <p className="text-muted-foreground">
            Manage all registered users, their plans, and activity
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-500/10 flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats ? stats.free : users.filter(u => u.plan_id === 'free' || !u.plan_id).length}</p>
                <p className="text-xs text-muted-foreground">Free Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Crown className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats ? stats.premium : users.filter(u => u.plan_id === 'premium').length}</p>
                <p className="text-xs text-muted-foreground">Premium Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                {/* WO-24 — this counted users.plan_id === "enterprise", a value
                    the app refuses to write because Enterprise comes from a
                    SEAT, so it was permanently zero while seat-holders existed.
                    The server now counts seat-holders. */}
                <p className="text-2xl font-bold">{stats ? stats.enterprise : 0}</p>
                <p className="text-xs text-muted-foreground">Enterprise Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Plans" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="standard">Standard</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
                {/* WO-71 */}
                <SelectItem value="ambassador">Ambassadors</SelectItem>
                <SelectItem value="first_fifty">First Fifty</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {/* WO-21 — the platform-wide role is stored as "admin", and
                    everyone else as "member". This filter used to send
                    role=user, a value no account has ever had, so picking
                    "User" returned an empty screen (0 of 29 accounts live). */}
                <SelectItem value="admin">MID2BID Admin</SelectItem>
                <SelectItem value="member">User</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={handleSearch}>
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            
            <Button variant="outline" onClick={fetchUsers}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Registered Users</CardTitle>
          <CardDescription>
            Showing {users.length} of {total} users
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Activity</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((userData) => (
                    <TableRow key={userData.id}
                      className={userData.promo && userData.promo.status !== 'invited' && promoStyles[userData.promo.kind] ? promoStyles[userData.promo.kind].row : ''}
                      data-testid={userData.promo ? `promo-row-${userData.id}` : undefined}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-medium">
                              {userData.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{userData.name || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground">{userData.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{userData.company || '-'}</span>
                      </TableCell>
                      <TableCell>
                        {/* WO-24 — the resolved plan: a seat-holder is
                            Enterprise even though users.plan_id never says so. */}
                        <div className="flex flex-col items-start gap-1">
                          <Badge className={planColors[userData.resolved_plan || userData.plan_id] || planColors.free}>
                            {planNames[userData.resolved_plan || userData.plan_id] || 'Free'}
                          </Badge>
                          <PromoBadge promo={userData.promo} testid={`promo-badge-${userData.id}`} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {userData.role === 'admin' ? (
                          <Badge variant="outline" className="text-red-500 border-red-500/30">
                            <Shield className="w-3 h-3 mr-1" />
                            MID2BID Admin
                          </Badge>
                        ) : (
                          <Badge variant="outline">User</Badge>
                        )}
                        {userData.is_internal && (
                          <Badge variant="outline" className="ml-1 text-slate-500 border-slate-400/40" data-testid={`internal-badge-${userData.id}`}>
                            Internal
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span title="Projects">{userData.activity?.projects_created || 0} proj</span>
                          <span>|</span>
                          <span title="Tenders">{userData.activity?.tenders_created || 0} tend</span>
                          <span>|</span>
                          <span title="Contacts">{userData.activity?.contacts_created || 0} cont</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(userData.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openUserDetails(userData)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleInternal(userData)}
                            disabled={togglingInternal[userData.id]}
                            title={userData.is_internal
                              ? 'Internal account (staff/test) — click to count it as a customer again'
                              : 'Mark as internal (staff/test): leave it out of the business figures'}
                            data-testid={`toggle-internal-btn-${userData.id}`}
                          >
                            <FlaskConical className={`w-4 h-4 ${userData.is_internal ? 'text-slate-600' : 'text-muted-foreground/50'}`} />
                          </Button>
                          {/* WO-21 — a MID2BID Admin needs a plan of their own.
                              This button was greyed out on every admin row, so
                              the only way to give the operator account a plan
                              was to demote it first, set the plan, and promote
                              it again. The server never had that restriction
                              (PUT /admin/users/{id}/plan writes users.plan_id
                              whatever the target's role), and without a plan an
                              admin account resolves to Free and cannot see five
                              of the six markets. */}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openPlanDialog(userData)}
                            title="Change Plan"
                          >
                            <Crown className="w-4 h-4" />
                          </Button>
                          {/* Role change (Batch D6 §1): promote a member to admin
                              or demote an admin to member. Self-change disabled;
                              the backend also refuses removing the last admin. */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                title={userData.role === 'admin'
                                  ? tt('Change to User', 'تغيير إلى مستخدم')
                                  : tt('Make MID2BID Admin', 'ترقية إلى مسؤول MID2BID')}
                                disabled={userData.id === user?.id || changingRole[userData.id]}
                                data-testid={`change-role-btn-${userData.id}`}
                              >
                                {changingRole[userData.id] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : userData.role === 'admin' ? (
                                  <UserIcon className="w-4 h-4 text-amber-500" />
                                ) : (
                                  <Shield className="w-4 h-4 text-indigo-500" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid="change-role-dialog">
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {userData.role === 'admin'
                                    ? tt('Change to User', 'تغيير إلى مستخدم')
                                    : tt('Make MID2BID Admin', 'ترقية إلى مسؤول MID2BID')}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {userData.role === 'admin'
                                    ? tt(
                                        `Remove MID2BID Admin rights from ${userData.name || userData.email}? They become a regular user of their own company and lose access to every company's data and to the admin tools.`,
                                        `إزالة صلاحيات المسؤول من ${userData.name || userData.email}؟ سيصبح عضوًا عاديًا ويفقد الوصول إلى بيانات جميع الشركات وأدوات المسؤول.`
                                      )
                                    : tt(
                                        `Make ${userData.name || userData.email} a MID2BID Admin? A MID2BID Admin can read every company's data, change any plan, delete users and run maintenance.`,
                                        `جعل ${userData.name || userData.email} مسؤولًا؟ يمكن للمسؤولين قراءة بيانات جميع الشركات وتغيير أي خطة وحذف المستخدمين وتشغيل الصيانة.`
                                      )}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{tt('Cancel', 'إلغاء')}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleChangeRole(userData.id, userData.role === 'admin' ? 'member' : 'admin')}
                                  data-testid={`confirm-role-btn-${userData.id}`}
                                >
                                  {userData.role === 'admin'
                                    ? tt('Change to User', 'تغيير إلى مستخدم')
                                    : tt('Make MID2BID Admin', 'ترقية إلى مسؤول MID2BID')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          {/* Email change: admin-only, allowed on ANY row
                              including self (that is how duplicate-email
                              accounts get untangled). */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEmailDialog(userData)}
                            title={tt('Change Email', 'تغيير البريد الإلكتروني')}
                            data-testid={`change-email-btn-${userData.id}`}
                          >
                            <Mail className="w-4 h-4 text-sky-500" />
                          </Button>
                          {/* Reset monthly report credits (Batch R1 §5) — resets
                              the counter only; touches no ownership/plan/Stripe. */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                title={tt('Reset monthly report credits', 'إعادة ضبط رصيد التقارير الشهري')}
                                disabled={resettingCredits[userData.id]}
                                data-testid={`reset-credits-btn-${userData.id}`}
                              >
                                {resettingCredits[userData.id]
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : <RefreshCw className="w-4 h-4 text-violet-500" />}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid="reset-credits-dialog">
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {tt('Reset monthly report credits', 'إعادة ضبط رصيد التقارير الشهري')}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {tt(
                                    `Reset this month's free-report credits for ${userData.name || userData.email} back to their plan allowance? This resets the counter only — it does not grant, remove or change any report they own, and does not touch their plan, seats or billing.`,
                                    `إعادة ضبط رصيد التقارير المجانية لهذا الشهر لـ ${userData.name || userData.email} إلى مخصص خطته؟ هذا يعيد ضبط العداد فقط — لا يمنح أو يزيل أو يغيّر أي تقرير يملكه، ولا يمس خطته أو مقاعده أو الفوترة.`
                                  )}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{tt('Cancel', 'إلغاء')}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleResetCredits(userData.id)}
                                  data-testid={`confirm-reset-credits-btn-${userData.id}`}
                                >
                                  {tt('Reset credits', 'إعادة الضبط')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                title="Delete User"
                                disabled={userData.role === 'admin' || deletingUser[userData.id]}
                              >
                                {deletingUser[userData.id] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete User</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete {userData.name || userData.email}? 
                                  This will also delete their private content (contacts, opportunities, private projects/tenders).
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUser(userData.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete User
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              User Details
            </DialogTitle>
            <DialogDescription>
              View detailed information and activity records
            </DialogDescription>
          </DialogHeader>
          
          {loadingUserDetails ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : userDetails ? (
            <div className="space-y-6">
              {/* User Info Card */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-2xl font-bold">
                        {userDetails.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold">{userDetails.name || 'Unknown'}</h3>
                      <div className="flex items-center gap-2 text-muted-foreground mt-1">
                        <Mail className="w-4 h-4" />
                        {userDetails.email}
                      </div>
                      {userDetails.company && (
                        <div className="flex items-center gap-2 text-muted-foreground mt-1">
                          <Building2 className="w-4 h-4" />
                          {userDetails.company}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-muted-foreground mt-1">
                        <Calendar className="w-4 h-4" />
                        Joined: {formatDate(userDetails.created_at)}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <Badge className={planColors[userDetails.resolved_plan || userDetails.plan_id] || planColors.free}>
                        {planNames[userDetails.resolved_plan || userDetails.plan_id] || 'Free'}
                      </Badge>
                      <PromoBadge promo={userDetails.promo} testid="promo-badge-detail" />
                      {userDetails.role === 'admin' && (
                        <Badge variant="outline" className="text-red-500 border-red-500/30">
                          <Shield className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Activity Stats */}
              <div className="grid grid-cols-5 gap-3">
                <Card>
                  <CardContent className="p-3 text-center">
                    <FolderOpen className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                    <p className="text-2xl font-bold">{userDetails.activity_counts?.projects_created || 0}</p>
                    <p className="text-xs text-muted-foreground">Projects</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <FileText className="w-5 h-5 mx-auto text-emerald-500 mb-1" />
                    <p className="text-2xl font-bold">{userDetails.activity_counts?.tenders_created || 0}</p>
                    <p className="text-xs text-muted-foreground">Tenders</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <Contact className="w-5 h-5 mx-auto text-purple-500 mb-1" />
                    <p className="text-2xl font-bold">{userDetails.activity_counts?.contacts_created || 0}</p>
                    <p className="text-xs text-muted-foreground">Contacts</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <Briefcase className="w-5 h-5 mx-auto text-amber-500 mb-1" />
                    <p className="text-2xl font-bold">{userDetails.activity_counts?.opportunities_created || 0}</p>
                    <p className="text-xs text-muted-foreground">Opportunities</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <UserIcon className="w-5 h-5 mx-auto text-gray-500 mb-1" />
                    <p className="text-2xl font-bold">{userDetails.activity_counts?.saved_items || 0}</p>
                    <p className="text-xs text-muted-foreground">Saved</p>
                  </CardContent>
                </Card>
              </div>

              {/* Activity Records Tabs */}
              <Tabs defaultValue="projects">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="projects">Projects</TabsTrigger>
                  <TabsTrigger value="tenders">Tenders</TabsTrigger>
                  <TabsTrigger value="contacts">Contacts</TabsTrigger>
                  <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
                </TabsList>
                
                <TabsContent value="projects" className="mt-4">
                  {userDetails.activity_records?.projects?.length > 0 ? (
                    <div className="space-y-2">
                      {userDetails.activity_records.projects.map((p, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium">{p.canonical_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {p.country} | {p.stage} | {formatDate(p.created_at)}
                            </p>
                          </div>
                          {p.visibility === 'private' && (
                            <Badge variant="outline" className="text-amber-500">Private</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No projects created</p>
                  )}
                </TabsContent>
                
                <TabsContent value="tenders" className="mt-4">
                  {userDetails.activity_records?.tenders?.length > 0 ? (
                    <div className="space-y-2">
                      {userDetails.activity_records.tenders.map((t, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium">{t.title?.en || t.title?.ar || 'Untitled'}</p>
                            <p className="text-xs text-muted-foreground">
                              {t.country} | {t.status} | {formatDate(t.created_at)}
                            </p>
                          </div>
                          {t.visibility === 'private' && (
                            <Badge variant="outline" className="text-amber-500">Private</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No tenders created</p>
                  )}
                </TabsContent>
                
                <TabsContent value="contacts" className="mt-4">
                  {userDetails.activity_records?.contacts?.length > 0 ? (
                    <div className="space-y-2">
                      {userDetails.activity_records.contacts.map((c, i) => (
                        <div key={i} className="p-3 bg-muted/50 rounded-lg">
                          <p className="font-medium">{c.first_name} {c.last_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {c.company || 'No company'} | {formatDate(c.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No contacts created</p>
                  )}
                </TabsContent>
                
                <TabsContent value="opportunities" className="mt-4">
                  {userDetails.activity_records?.opportunities?.length > 0 ? (
                    <div className="space-y-2">
                      {userDetails.activity_records.opportunities.map((o, i) => (
                        <div key={i} className="p-3 bg-muted/50 rounded-lg">
                          <p className="font-medium">{o.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {o.stage} | ${o.value?.toLocaleString() || 0} | {formatDate(o.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No opportunities created</p>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5" />
              Change Subscription Plan
            </DialogTitle>
            <DialogDescription>
              Update the subscription plan for {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">Current Plan:</p>
              <Badge className={planColors[selectedUser?.plan_id] || planColors.free}>
                {planNames[selectedUser?.plan_id] || 'Free'}
              </Badge>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-medium">New Plan:</p>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* WO-22 — "Enterprise" was offered here and the server
                      refuses it: enterprise is not a personal plan, it comes
                      only from a seat on a company subscription
                      (PUT /admin/users/{id}/plan answers 400 and says so). An
                      option that always fails is worse than no option. */}
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="standard">Standard - $20/month</SelectItem>
                  <SelectItem value="premium">Premium - $49/month</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Enterprise is not set here — it comes from an Enterprise seat on
                the company's subscription.
              </p>
            </div>

            {/* WO-68 — Enterprise box */}
            {(() => {
              const ce = selectedUser?.company_enterprise || {};
              const holdsSeat = selectedUser?.resolved_plan === 'enterprise';
              const box = 'rounded-lg border border-purple-300 dark:border-purple-800 bg-purple-50/60 dark:bg-purple-950/20 p-3 text-xs space-y-2';
              const btn = (label, action, variant, testid) => (
                <Button size="sm" variant={variant} disabled={entBusy}
                  onClick={() => handleEnterpriseSeat(action)} data-testid={testid}>
                  {entBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Crown className="w-4 h-4 mr-2" />}
                  {label}
                </Button>
              );
              return (
                <div className={box} data-testid="enterprise-box">
                  <p className="font-semibold text-sm">Enterprise</p>
                  {holdsSeat ? (
                    <>
                      <p>
                        {ce.complimentary
                          ? `Holds a complimentary Enterprise seat (free, given by Mid2Bid). ${selectedUser?.company || 'The company'} has ${ce.in_use} free seat(s).`
                          : `Holds one of the ${ce.purchased_seats} Enterprise seats ${selectedUser?.company || 'the company'} pays for.`}
                      </p>
                      <p className="text-muted-foreground">
                        {ce.complimentary
                          ? (ce.in_use <= 1
                            ? 'Removing it ends the complimentary Enterprise of this company.'
                            : 'Removing it removes that free seat; the others keep theirs.')
                          : 'Removing it frees the seat; the company keeps paying for it and can give it to someone else.'}
                        {' '}They go back to their own plan ({planNames[selectedUser?.plan_id] || 'Free'}).
                      </p>
                      {btn('Remove Enterprise seat', 'remove', 'outline', 'enterprise-remove-btn')}
                    </>
                  ) : ce.paid ? (
                    ce.free_seats > 0 ? (
                      <>
                        <p>{selectedUser?.company || 'The company'} pays for Enterprise: {ce.in_use} of {ce.purchased_seats} seats in use.</p>
                        {btn('Give one of the company\u2019s paid seats', 'paid', 'default', 'enterprise-paid-seat-btn')}
                      </>
                    ) : (
                      <p>{selectedUser?.company || 'The company'} pays for Enterprise and all {ce.purchased_seats} seats are in use. A free seat cannot be added to a paid subscription: remove a seat from someone else, or the company buys more.</p>
                    )
                  ) : (
                    <>
                      <p>
                        Make this person Enterprise for free (complimentary): every Enterprise feature, no payment, no end date — it lasts until you remove it here.
                        {ce.complimentary ? ` ${selectedUser?.company || 'The company'} already has ${ce.in_use} complimentary seat(s); this adds one.` : ''}
                      </p>
                      <p className="text-muted-foreground">
                        They see colleagues' CRM records only through a Team, like everyone else. White-label Word files still have to be bought.
                      </p>
                      {btn('Give complimentary Enterprise', 'complimentary', 'default', 'enterprise-complimentary-btn')}
                    </>
                  )}
                </div>
              );
            })()}

            {/* WO-71 — First Fifty / Ambassador box */}
            {(() => {
              const pr = selectedUser?.promo;
              const st = pr ? promoStyles[pr.kind] : null;
              const busyIcon = (Icon) => (promoBusy
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Icon className="w-4 h-4 mr-2" />);
              return (
                <div className={`rounded-lg border p-3 text-xs space-y-2 ${st ? st.box : 'border-slate-300 dark:border-slate-700'}`}
                  data-testid="promo-box">
                  <p className="font-semibold text-sm">First Fifty / Ambassador</p>
                  {pr && pr.status === 'invited' ? (
                    <>
                      <p>
                        Invited as <strong>{st?.label}</strong> on {fmtDay(pr.invited_at)}. Their free Premium starts the moment
                        they accept in the app (they see the invitation when they sign in, and got an e-mail).
                      </p>
                      <Button size="sm" variant="outline" disabled={promoBusy} onClick={() => handlePromo('end')} data-testid="promo-cancel-invite-btn">
                        Cancel invitation
                      </Button>
                    </>
                  ) : pr ? (
                    <>
                      <p>
                        <strong>{st?.label}</strong>: Premium free until <strong>{fmtDay(pr.ends_at)}</strong> ({pr.days_left} days left),
                        then back to {planNames[pr.original_plan] || 'Free'} automatically. Reminders go to them 14, 7 and 1 day before.
                        {pr.premium_only ? ' They chose Premium only (no commissions).' : ''}
                      </p>
                      <p className="text-muted-foreground">
                        New paying users from their referral code since {fmtDay(pr.period_started_at)}: <strong>{pr.paying_referrals}</strong>
                        {pr.kind === 'ambassador' ? ' (more than 100 = it renews by itself for another year).' : '.'}
                        {' '}Changing the plan above by hand ends this period.
                        {pr.invitation ? ` Invited as ${promoStyles[pr.invitation.kind]?.label} — waiting for them to accept.` : ''}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {pr.kind === 'ambassador' && (
                          <Button size="sm" disabled={promoBusy} onClick={() => handlePromo('ambassador')} data-testid="promo-renew-btn">
                            {busyIcon(Award)}Renew 12 months now
                          </Button>
                        )}
                        {pr.kind === 'first_fifty' && !pr.invitation && (
                          <Button size="sm" disabled={promoBusy} onClick={() => handlePromo('ambassador')} data-testid="promo-upgrade-btn">
                            {busyIcon(Award)}Invite as Ambassador
                          </Button>
                        )}
                        <Button size="sm" variant="outline" disabled={promoBusy} onClick={() => handlePromo('end')} data-testid="promo-end-btn">
                          End now
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p>
                        Invite this person to free Premium for a limited time. They get an e-mail and see the invitation in the app;
                        the free Premium starts when they accept (with the Referral Programme Terms). The app works out the end date,
                        sends the reminders (14, 7 and 1 day before) and moves them back to their current plan
                        ({planNames[selectedUser?.plan_id] === 'Premium' ? 'Free' : (planNames[selectedUser?.plan_id] || 'Free')}) when it ends.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" disabled={promoBusy} onClick={() => handlePromo('first_fifty')} data-testid="promo-first-fifty-btn">
                          {busyIcon(Rocket)}Invite as First Fifty (90 days)
                        </Button>
                        <Button size="sm" disabled={promoBusy} onClick={() => handlePromo('ambassador')} data-testid="promo-ambassador-btn">
                          {busyIcon(Award)}Invite as Ambassador (12 months)
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* WO-22 — a downgrade can cost this person their Team, and the
                administrator doing it should know before clicking, not after. */}
            {['premium', 'enterprise'].includes((selectedUser?.plan_id || 'free').toLowerCase())
              && ['free', 'standard'].includes((newPlan || '').toLowerCase()) && (
              <div
                className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50/70 dark:bg-amber-950/30 p-3 text-xs"
                data-testid="downgrade-team-warning"
              >
                <p className="font-semibold">This is a downgrade.</p>
                {/* WO-24 — this described what WO-22 did, and WO-23 changed it.
                    Nobody is removed from a Team by a plan change any more. */}
                <p className="mt-1">
                  A Team needs Premium or an Enterprise seat. If this person is in
                  a Team their membership is PAUSED straight away: their
                  colleagues stop seeing the contacts and opportunities they
                  created, and they see only their own. They keep their place on
                  the Team, nothing is deleted, and they come back automatically
                  if they subscribe again — no new invitation needed. If they are
                  the Team Admin the role passes to the longest-standing member
                  who is still subscribed; if nobody in the Team is subscribed,
                  the Team is suspended and they keep the role.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdatePlan} disabled={updatingPlan}>
              {updatingPlan ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Plan'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Change Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent data-testid="change-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {tt('Change Email Address', 'تغيير عنوان البريد الإلكتروني')}
            </DialogTitle>
            <DialogDescription>
              {tt('Update the email for', 'تحديث البريد الإلكتروني لـ')} {emailTarget?.name || emailTarget?.email}. {tt('Password, sessions and verification status are left unchanged.', 'تبقى كلمة المرور والجلسات وحالة التحقق دون تغيير.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">{tt('Current email', 'البريد الحالي')}:</p>
              <p className="text-sm text-muted-foreground break-all">{emailTarget?.email}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">{tt('New email', 'البريد الجديد')}:</p>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUpdateEmail()}
                placeholder="you@company.com"
                data-testid="change-email-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              {tt('Cancel', 'إلغاء')}
            </Button>
            <Button
              onClick={handleUpdateEmail}
              disabled={updatingEmail || !newEmail.trim()}
              data-testid="change-email-confirm-btn"
            >
              {updatingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {tt('Saving...', 'جارٍ الحفظ...')}
                </>
              ) : (
                tt('Update Email', 'تحديث البريد')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
